import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { IconButton, InputLabel, TextField } from '@mui/material';
import { removeWhitespaceSpecialCharacters } from 'utils/stringUtil';
import ClearIcon from '@mui/icons-material/Clear';

type TextFilterProps = {
  filterName: string;
  placeholderText: string;
  dispatchAction: any;
  defaultValue?: string;
  id?: string;
  customOnchange?: any;
  customKeyDownEvent?: any;
  debounceMs?: number;
  loading?: boolean;
  disabled?: boolean;
};

/**
 * A component that allows filtering the partner list via the text field input
 * Uses debouncing (only dispatches to Redux after the user stops typing)
 * @param filterName The name of the filter, displayed in the label
 * @param placeholderText The text to display if the field is empty
 * @param defaultValue The default value of the filter
 * @param dispatchAction The action to be executed,
 * @param id: the id of the textbox in the DOM
 * @param customOnchange: on change to be executed on change
 * @param loading: whether the text filter should indicate a circular progress bar for loading
 * @param debounceMs: time to dispatch the actions after. Default value is 500.
 */
export const TextFilter = ({
  filterName,
  placeholderText,
  defaultValue,
  dispatchAction,
  id,
  customOnchange,
  debounceMs = 500,
  disabled = false,
}: TextFilterProps) => {
  const dispatch = useDispatch();

  const [value, setValue] = useState('');
  const [onChange$] = useState(new Subject<string>());

  const handleChange = (value: string) => {
    setValue(value);
    onChange$.next(value);
  };

  // defaultValue
  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
      onChange$.next(defaultValue);
    }
  }, [defaultValue, setValue, onChange$]);

  // debounce redux action
  const subscription = onChange$.pipe(debounceTime(debounceMs), distinctUntilChanged()).subscribe((val) => {
    dispatchAction(val);
  });

  useEffect(() => {
    return () => subscription.unsubscribe();
  }, [onChange$, dispatch, filterName]);

  return (
    <>
      <InputLabel htmlFor={id ?? `${removeWhitespaceSpecialCharacters(filterName)}-filter-input`}>
        {filterName}
      </InputLabel>
      <TextField
        id={id ?? `${removeWhitespaceSpecialCharacters(filterName)}-filter-input`}
        variant="filled"
        placeholder={placeholderText}
        fullWidth
        disabled={disabled}
        value={value}
        autoComplete="false"
        inputProps={{
          autoComplete: 'off',
        }}
        onChangeCapture={(event: any) => (customOnchange ? customOnchange(event.currentTarget) : undefined)}
        onChange={(event) => handleChange(event.target.value)}
        InputProps={{
          endAdornment: (
            <IconButton
              disableRipple
              disableFocusRipple
              about="test"
              onClickCapture={() => (customOnchange ? customOnchange('') : handleChange(''))}
              onClick={() => handleChange('')}
            >
              <ClearIcon />
            </IconButton>
          ),
        }}
      />
    </>
  );
};
